/** 24-hour date + time picker (calendar + analog clock popup).
 *
 * Drop-in replacement for the old native DateTime24Picker: same
 * `value` (ISO string, "" when unset) / `onChange(iso)` / `minDate`
 * contract, so call sites swap with no logic change. `ampm={false}`
 * forces a 24h clock; `renderTimeViewClock` gives the analog clock
 * widget for the time view.
 *
 * READS AND WRITES IN THE ORG'S TIME ZONE, not the browser's.
 *
 * Found in production 2026-09-27, operator's laptop on Asia/Karachi and
 * the org on Europe/Paris: the slot grid under this field renders org
 * time (via `formatTime`), while a bare `dayjs(value)` renders browser
 * time. Clicking the slot labelled "11:00" put "14:00" in this field —
 * three hours apart, with nothing saying either number was zone-
 * specific. The stored instant was right, so the call would have
 * happened in the slot actually clicked; the operator just could not
 * read it. Typing was worse: the same label meant a different instant
 * from the one the grid showed.
 *
 * One zone everywhere on the page: whatever `getDisplayTimeZone()`
 * reports, which is the zone the org schedules in and the zone the
 * beneficiary's invitation is written in.
 *
 * MOVES IN SLOT STEPS when given `minutesStep` (reported 2026-10-18).
 * The clock used to move one minute at a time, so an operator could
 * book 11:31 — a time that straddles two 30-minute slots and blocks
 * both. The clock now only offers grid minutes, and anything typed is
 * snapped to the nearest slot start. The server refuses an off-grid
 * time too; this is so the operator never gets that far.
 */
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utcPlugin from "dayjs/plugin/utc";

import { getDisplayTimeZone } from "@/lib/format";
import { snapToGrid } from "@/lib/slotGrid";

import { MuiPickersProvider } from "./MuiPickers";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

type Props = {
  /** ISO string, or "" when unset. */
  value: string;
  onChange: (iso: string) => void;
  /** Earliest selectable day, "YYYY-MM-DD". */
  minDate?: string;
  disabled?: boolean;
  /** Slot length in minutes. Unset = minute precision (non-booking uses). */
  minutesStep?: number;
  /** Where the grid starts within the hour — 15 when the org opens at
   *  08:15 with 30-minute slots. 0 for the usual on-the-hour opening. */
  offsetMinutes?: number;
};



export function MuiDateTimePicker({
  value,
  onChange,
  minDate,
  disabled,
  minutesStep,
  offsetMinutes = 0,
}: Props) {
  // No org zone configured — fall back to the browser, which is what
  // every other date surface in the app already does.
  const zone = getDisplayTimeZone();
  const step = minutesStep && minutesStep > 1 ? minutesStep : undefined;
  // MUI's step props count from :00, so they can only describe a grid
  // that starts on the hour. An offset grid still snaps in onChange.
  const onTheHour = step !== undefined && offsetMinutes % step === 0;

  return (
    <MuiPickersProvider>
      <DateTimePicker
        ampm={false}
        timezone={zone}
        value={value ? (zone ? dayjs(value).tz(zone) : dayjs(value)) : null}
        minDateTime={
          minDate
            ? (zone ? dayjs.tz(minDate, zone) : dayjs(minDate)).startOf("day")
            : undefined
        }
        disabled={disabled}
        {...(onTheHour && step ? { minutesStep: step, timeSteps: { minutes: step } } : {})}
        onChange={(d) => {
          if (!d || !d.isValid()) {
            onChange("");
            return;
          }
          const v = step ? snapToGrid(d, step, offsetMinutes) : d;
          onChange(v.toDate().toISOString());
        }}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
        }}
        slotProps={{ textField: { size: "small", fullWidth: true } }}
      />
    </MuiPickersProvider>
  );
}
